import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import useCrossTabDataRefreshToken from "../../hooks/useCrossTabDataRefresh";
import CorporateRecordBadge from "../components/CorporateRecordBadge";
import { managedByAdmin } from "../utils/tenderManagement";
import AddSupplierPopup from "../components/AddSupplierPopup";
import EditSupplierPopup from "../components/EditSupplierPopup";
import SupplierMergePopup, { supplierGroupMembers, supplierGroupSearchText } from "../components/SupplierMergePopup";
import { supplierMergeText } from "../components/supplierMergeMessages";
import ImportSuppliersPopup from "../components/ImportSuppliersPopup";
import { normalizeSupplierImportRows, sortSuppliersList, supplierEmailsToText } from "../components/suppliersUi";
import useSupplierAccess from "../components/useSupplierAccess";
import { supplierDate, supplierPermissions, supplierTranslator } from "../components/supplierMessages";
import "../PurchasingUi.css";
import "../components/SupplierWorkspace.css";

const searchText = (row) => [row.supplier_id, row.name, row.contact_name, supplierEmailsToText(row.email), row.phone, row.tax_id, row.address, supplierGroupSearchText(row)].filter(Boolean).join(" ").toLowerCase();

export default function Suppliers() {
  const { themeId, languageId } = usePmsTheme();
  const supplierText = supplierTranslator(languageId);
  const mergeText = supplierMergeText(languageId);
  const t = (key, values) => mergeText(key) !== key ? mergeText(key, values) : supplierText(key, values);
  const [searchParams] = useSearchParams();
  const user = readStoredUser();
  const businessUnitId = String(searchParams.get("business_unit_id") || user?.active_business_unit_id || "");
  const scopeAccess = useSupplierAccess(businessUnitId);
  const permissions = supplierPermissions(user);
  const access = Object.fromEntries(Object.entries(permissions).map(([key, allowed]) => [key, allowed && scopeAccess.can("purchasing.suppliers.view", true)]));
  const viewPermission = "purchasing.suppliers.view";
  const exportPermissions = [viewPermission, "purchasing.suppliers.export"];
  const importPermissions = [viewPermission, "purchasing.suppliers.create", "purchasing.suppliers.import"];
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ key: "", rows: [], ready: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [usageFilter, setUsageFilter] = useState("all");
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [merging, setMerging] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const inputRef = useRef(null);
  const loadRequest = useRef(0);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();
  const filterRevision = useRef({ key: "", epoch: 0 });
  const filterKey = JSON.stringify([scopeAccess.contextKey, search, statusFilter, usageFilter]);
  if (filterRevision.current.key !== filterKey) filterRevision.current = { key: filterKey, epoch: filterRevision.current.epoch + 1 };
  const ready = data.key === scopeAccess.contextKey && data.ready && access.view;
  const rows = ready ? data.rows : [];

  const fetchSuppliers = useCallback(async ({ quiet = false } = {}) => {
    const request = ++loadRequest.current;
    if (!scopeAccess.can(viewPermission, true)) { setData({ key: "", rows: [], ready: false }); setLoading(false); return; }
    const stamp = scopeAccess.capture();
    if (!quiet) setLoading(true);
    setLoadError(false);
    try {
      const config = scopeAccess.requestConfig(viewPermission);
      const response = await axios.get("/api/purchasing/suppliers", { ...config, params: { ...config.params, usage: "all" } });
      if (!Array.isArray(response.data)) throw new Error("Invalid supplier response");
      if (request !== loadRequest.current || !scopeAccess.isCurrent(stamp, viewPermission)) return;
      setData({ key: scopeAccess.contextKey, rows: sortSuppliersList(response.data), ready: true });
    } catch (_error) {
      if (request === loadRequest.current && scopeAccess.isCurrent(stamp, viewPermission)) setLoadError(true);
    } finally {
      if (request === loadRequest.current && scopeAccess.isCurrent(stamp, viewPermission)) setLoading(false);
    }
  }, [scopeAccess]);

  useEffect(() => {
    setData({ key: "", rows: [], ready: false }); setExporting(false);
    setAdding(false); setEditing(null); setMerging(false); setImportFile(null); setNotice(null);
    setSearch(""); setStatusFilter("all"); setUsageFilter("all");
    fetchSuppliers();
    return () => { loadRequest.current += 1; };
  }, [fetchSuppliers]);
  const refreshToken = useRef(crossTabRefreshToken);
  useEffect(() => {
    if (refreshToken.current === crossTabRefreshToken) return;
    refreshToken.current = crossTabRefreshToken;
    fetchSuppliers({ quiet: true });
  }, [crossTabRefreshToken, fetchSuppliers]);
  useEffect(() => {
    const refresh = () => fetchSuppliers({ quiet: true });
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [fetchSuppliers]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data.key === scopeAccess.contextKey && data.ready && access.view ? data.rows : []).filter((supplier) =>
      (statusFilter === "all" || Boolean(supplier.is_active) === (statusFilter === "active")) &&
      (usageFilter === "all" || (usageFilter === "stock" && supplier.is_stock_supplier !== false) ||
        (usageFilter === "service" && supplier.is_service_supplier === true)) &&
      (!query || searchText(supplier).includes(query)));
  }, [data, scopeAccess.contextKey, access.view, search, statusFilter, usageFilter]);

  const exportRows = async () => {
    if (exporting || !ready || !filtered.length || !scopeAccess.can(exportPermissions, true)) return;
    const stamp = scopeAccess.capture(), revision = filterRevision.current.epoch;
    const current = () => revision === filterRevision.current.epoch && scopeAccess.isCurrent(stamp, exportPermissions);
    setExporting(true);
    try {
      const config = scopeAccess.requestConfig(exportPermissions);
      const response = await axios.get("/api/purchasing/suppliers", { ...config, params: { ...config.params, usage: "all", export: 1 } });
      if (!current()) return;
      if (!Array.isArray(response.data)) throw new Error("Invalid supplier response");
      const query = search.trim().toLowerCase();
      const exported = sortSuppliersList(response.data).filter((supplier) =>
        (statusFilter === "all" || Boolean(supplier.is_active) === (statusFilter === "active")) &&
        (usageFilter === "all" || (usageFilter === "stock" && supplier.is_stock_supplier !== false) ||
          (usageFilter === "service" && supplier.is_service_supplier === true)) &&
        (!query || searchText(supplier).includes(query)));
      // Keep machine-readable headers so exported files can be imported again.
      const worksheet = XLSX.utils.json_to_sheet(exported.map((supplier) => ({
        supplier_id: supplier.supplier_id || "", name: supplier.name || "", contact_name: supplier.contact_name || "",
        email: supplierEmailsToText(supplier.email), phone: supplier.phone || "", tax_id: supplier.tax_id || "",
        address: supplier.address || "", is_active: Boolean(supplier.is_active),
        minimum_order_amount: supplier.minimum_order_amount == null ? "" : Number(supplier.minimum_order_amount),
        is_stock_supplier: supplier.is_stock_supplier !== false, is_service_supplier: supplier.is_service_supplier === true,
        created_at: supplier.created_at || "", updated_at: supplier.updated_at || "",
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Suppliers");
      if (!current()) return;
      XLSX.writeFile(workbook, "suppliers.xlsx");
      setNotice({ tone: "success", key: "{count} suppliers exported.", values: { count: exported.length } });
    } catch (_error) { if (current()) setNotice({ tone: "error", key: "The spreadsheet could not be exported." }); }
    finally { if (scopeAccess.isCurrent(stamp, exportPermissions)) setExporting(false); }
  };

  const readImport = (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file || !scopeAccess.can(importPermissions, true)) { input.value = ""; return; }
    const stamp = scopeAccess.capture();
    const reader = new FileReader();
    const fail = () => {
      if (scopeAccess.isCurrent(stamp, importPermissions)) setNotice({ tone: "error", key: "The file could not be read. Check the format and try again." });
      input.value = "";
    };
    reader.onload = (loaded) => {
      if (!scopeAccess.isCurrent(stamp, importPermissions)) return;
      try {
        const workbook = XLSX.read(new Uint8Array(loaded.target.result), { type: "array", raw: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsed = normalizeSupplierImportRows(XLSX.utils.sheet_to_json(sheet, { defval: "", blankrows: false }));
        if (!parsed.length) setNotice({ tone: "error", key: "The selected file has no recognizable supplier rows." });
        else { setImportFile({ name: file.name, rows: parsed }); setNotice(null); }
      } catch (_error) { fail(); }
      finally { input.value = ""; }
    };
    reader.onerror = fail;
    reader.readAsArrayBuffer(file);
  };

  const saved = (supplier, isNew) => {
    if (!scopeAccess.can(viewPermission, true)) return;
    if (!supplier?.id) { fetchSuppliers({ quiet: true }); return; }
    setData((previous) => previous.key !== scopeAccess.contextKey ? previous : ({
      ...previous, rows: sortSuppliersList(isNew ? [supplier, ...previous.rows.filter((row) => row.id !== supplier.id)] :
        previous.rows.map((row) => row.id === supplier.id ? supplier : row)),
    }));
    setNotice({ tone: "success", key: isNew ? "Supplier created." : "Supplier updated." });
    fetchSuppliers({ quiet: true });
  };
  const imported = async (count) => {
    if (!scopeAccess.can(viewPermission, true)) return;
    setImportFile(null);
    setNotice({ tone: count ? "success" : "info", key: count ? "{count} suppliers imported." : "Import finished without adding new suppliers.", values: { count } });
    await fetchSuppliers({ quiet: true });
  };
  const status = (supplier) => <span className={"supplier-status supplier-status--" + (supplier.is_active ? "active" : "inactive")}>{t(supplier.is_active ? "Active" : "Inactive")}</span>;
  const usage = (supplier) => [
    ...(supplier.is_stock_supplier !== false ? [t("Stock and materials")] : []),
    ...(supplier.is_service_supplier === true ? [t("Services")] : []),
  ].join(" · ") || "—";
  const restriction = !access.view ? "You do not have permission to view suppliers." : !businessUnitId ? "Select a business unit to manage suppliers." : null;
  const canOpen = access.view && ready && !loading;

  return <section className="pilcore-purchasing-ui suppliers-workspace" data-theme={themeId} aria-label={t("Suppliers")}>
    <header className="suppliers-header">
      <h1>{t("Suppliers")}</h1>
      {!restriction && <div className="suppliers-actions">
        {access.merge && <button type="button" className="supplier-button" onClick={() => setMerging(true)} disabled={!canOpen || rows.length < 2}>{t("Merge suppliers")}</button>}
        {access.export && <button type="button" className="supplier-button" onClick={exportRows} disabled={!canOpen || !filtered.length || exporting}>{t("Export Excel")}</button>}
        {access.import && <><button type="button" className="supplier-button" onClick={() => inputRef.current?.click()} disabled={!canOpen}>{t("Import Excel/CSV")}</button>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={readImport} aria-label={t("Import Excel/CSV")} />
        </>}
        {access.create && <button type="button" className="supplier-button supplier-button--primary" onClick={() => setAdding(true)} disabled={!canOpen}>+ {t("Add supplier")}</button>}
      </div>}
    </header>
    {restriction ? <div className="suppliers-empty" role="alert">{t(restriction)}</div> : <>
      <div className="suppliers-toolbar">
        <label className="suppliers-search"><span className="supplier-sr-only">{t("Search suppliers")}</span><input type="search" value={search} onChange={(event) => { filterRevision.current.epoch += 1; setSearch(event.target.value); }} placeholder={t("Name, code, contact, email, phone or tax ID…")} /></label>
        <div className="suppliers-status-filter" role="group" aria-label={t("Supplier status")}>
          {["all", "active", "inactive"].map((key) => <button key={key} type="button" className="supplier-button" aria-pressed={statusFilter === key} onClick={() => { filterRevision.current.epoch += 1; setStatusFilter(key); }}>{t(key === "all" ? "All" : key === "active" ? "Active" : "Inactive")}</button>)}
        </div>
        <div className="suppliers-status-filter" role="group" aria-label={t("Supplier usage")}>
          {["all", "stock", "service"].map((key) => <button key={key} type="button" className="supplier-button" aria-pressed={usageFilter === key}
            onClick={() => { filterRevision.current.epoch += 1; setUsageFilter(key); }}>{t(key === "all" ? "All uses" : key === "stock" ? "Stock and materials" : "Services")}</button>)}
        </div>
        <span className="suppliers-count" aria-live="polite">{t("Showing {visible} of {total}", { visible: filtered.length, total: rows.length })}</span>
      </div>
      {notice && <div className={"supplier-feedback supplier-feedback--" + notice.tone} role={notice.tone === "error" ? "alert" : "status"}>{t(notice.key, notice.values)}</div>}
      {loadError && <div className="supplier-feedback supplier-feedback--error" role="alert">{t(ready ? "The list could not be updated. Previously loaded data is shown." : "Suppliers could not be loaded. Try again.")}
        <button type="button" className="supplier-button" onClick={() => fetchSuppliers()}>{t("Try again")}</button>
      </div>}
      {loading || (!ready && !loadError) ? <div className="suppliers-empty" role="status">{t("Loading suppliers…")}</div> : !ready ? null : !filtered.length ?
        <div className="suppliers-empty"><span>{t(rows.length ? "No suppliers match these filters." : "No suppliers yet.")}</span>
          {(search || statusFilter !== "all" || usageFilter !== "all") && <button type="button" className="supplier-button" onClick={() => { filterRevision.current.epoch += 1; setSearch(""); setStatusFilter("all"); setUsageFilter("all"); }}>{t("Clear filters")}</button>}
        </div> :
        <div className="suppliers-table-scroll" tabIndex={0} role="region" aria-label={t("Suppliers")}>
          <table className="supplier-table"><colgroup>{[96, 175, 120, 175, 110, 105, 165, 85, 95, ...(access.edit ? [80] : [])].map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
            <thead><tr>{["Code", "Supplier", "Contact", "Email", "Phone", "Tax ID", "Address", "Status", "Updated", ...(access.edit ? ["Actions"] : [])].map((label) => <th key={label} scope="col">{t(label)}</th>)}</tr></thead>
            <tbody>{filtered.map((supplier) => <tr key={supplier.id}>
              <td><span className="supplier-code">{supplier.supplier_id || "SUP-" + supplier.id}</span><CorporateRecordBadge record={supplier} /></td>
              <td>{access.edit && !managedByAdmin(supplier) ? <button type="button" className="supplier-name" onClick={() => setEditing(supplier)}>{supplier.name || "—"}</button> : <strong>{supplier.name || "—"}</strong>}<div>{usage(supplier)}</div>{supplierGroupMembers(supplier).length > 1 && <details className="supplier-group"><summary>{t("Grouped suppliers ({count})", { count: supplierGroupMembers(supplier).length })}</summary><ul>{supplierGroupMembers(supplier).filter((member) => Number(member.id) !== Number(supplier.id)).map((member) => <li key={member.id}>{member.supplier_id || t("No supplier code")} · {member.name} · {t(managedByAdmin(member) ? "Corporate" : "Local")}</li>)}</ul></details>}</td>
              <td>{supplier.contact_name || "—"}</td><td>{supplierEmailsToText(supplier.email) || "—"}</td>
              <td>{supplier.phone || "—"}</td><td>{supplier.tax_id || "—"}</td><td>{supplier.address || "—"}</td>
              <td>{status(supplier)}</td><td>{supplierDate(supplier.updated_at, languageId)}</td>
              {access.edit && <td><button type="button" className="supplier-button" onClick={() => { if (!managedByAdmin(supplier)) setEditing(supplier); }}
                disabled={managedByAdmin(supplier)} title={managedByAdmin(supplier) ? t("Managed by general administration. Edit this supplier in the corporate catalog.") : undefined}>{t("Edit")}</button></td>}
            </tr>)}</tbody>
          </table>
        </div>}
    </>}
    {merging && access.merge && canOpen && <SupplierMergePopup suppliers={rows} businessUnitId={businessUnitId} onClose={() => setMerging(false)} onMerged={() => {
      setMerging(false); setNotice({ tone: "success", key: "Supplier grouping saved." }); fetchSuppliers({ quiet: true });
    }} />}
    {adding && access.create && canOpen && <AddSupplierPopup isOpen businessUnitId={businessUnitId} onClose={() => setAdding(false)} onAdded={(supplier) => saved(supplier, true)} />}
    {editing && access.edit && canOpen && <EditSupplierPopup isOpen businessUnitId={businessUnitId} supplier={editing} onClose={() => setEditing(null)} onUpdated={(supplier) => saved(supplier, false)} />}
    {importFile && access.import && canOpen && <ImportSuppliersPopup isOpen rows={importFile.rows} fileName={importFile.name} businessUnitId={businessUnitId} onClose={() => setImportFile(null)} onImported={imported} />}
  </section>;
}
